/**
 * ExecutiveActionPanel.js — Institutional Executive Action Intelligence Component
 * Bloomberg/NOC-style. Shows: Severity, Action, Financial Exposure,
 * Strategic Impact, Timeline, Owner, Blocker, Value Upside, CTA.
 */
import React, { useState } from "react";
import { AlertTriangle, TrendingUp, Clock, User, ArrowRight, ChevronDown, ChevronUp, Zap } from "lucide-react";

const SEVERITY_STYLE = {
  Critical: { bg:"#7f1d1d20", border:"#ef444440", label:"#ef4444", dot:"#ef4444" },
  High:     { bg:"#78350f20", border:"#f59e0b40", label:"#f59e0b", dot:"#f59e0b" },
  Medium:   { bg:"#1e3a5f20", border:"#60a5fa40", label:"#60a5fa", dot:"#60a5fa" },
  Monitor:  { bg:"#1e293b",   border:"#47556940", label:"#64748b", dot:"#475569" },
};

export function ExecutiveAction({ action, defaultExpanded = false }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const sev = SEVERITY_STYLE[action.severity] || SEVERITY_STYLE.Monitor;
  const testId = `action-${(action.title||"").toLowerCase().replace(/\s+/g,"-").substring(0,30)}`;

  return (
    <div style={{ background:sev.bg, border:`1px solid ${sev.border}`, borderLeft:`3px solid ${sev.dot}`,
      borderRadius:8, overflow:"hidden" }} data-testid={testId}>
      <div style={{ padding:"12px 14px" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:6 }}>
          <div style={{ flex:1 }}>
            <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4 }}>
              <span style={{ fontSize:8, fontWeight:800, color:sev.label, textTransform:"uppercase",
                letterSpacing:".07em", background:sev.label+"15", padding:"1px 6px", borderRadius:3 }}>
                {action.severity}
              </span>
              {action.category && (
                <span style={{ fontSize:8, color:"#475569", fontWeight:600 }}>{action.category}</span>
              )}
            </div>
            <p style={{ fontSize:10, fontWeight:700, color:"#f1f5f9", lineHeight:1.4, marginBottom:2 }}>{action.title}</p>
            <p style={{ fontSize:9, color:"#64748b", lineHeight:1.4 }}>{action.description}</p>
          </div>
          {action.financialExposure && (
            <div style={{ textAlign:"right", flexShrink:0, marginLeft:12 }}>
              <p style={{ fontSize:8, color:"#475569", marginBottom:1 }}>Exposure / Upside</p>
              <p style={{ fontSize:12, fontWeight:900, color:sev.label }}>{action.financialExposure}</p>
            </div>
          )}
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:6, marginBottom:8 }}>
          {action.timeline && (
            <div style={{ display:"flex", gap:4, alignItems:"center" }}>
              <Clock size={9} color="#475569" />
              <span style={{ fontSize:8, color:"#64748b" }}>{action.timeline}</span>
            </div>
          )}
          {action.owner && (
            <div style={{ display:"flex", gap:4, alignItems:"center" }}>
              <User size={9} color="#475569" />
              <span style={{ fontSize:8, color:"#64748b" }}>{action.owner}</span>
            </div>
          )}
          {action.valueUpside && (
            <div style={{ display:"flex", gap:4, alignItems:"center" }}>
              <TrendingUp size={9} color="#22c55e" />
              <span style={{ fontSize:8, color:"#22c55e", fontWeight:600 }}>{action.valueUpside}</span>
            </div>
          )}
        </div>

        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div style={{ background:"#FFCB0015", border:"1px solid #FFCB0025", borderRadius:5, padding:"4px 10px",
            display:"flex", alignItems:"center", gap:5, flex:1, marginRight:8 }}>
            <ArrowRight size={9} color="#FFCB00" />
            <p style={{ fontSize:9, color:"#FFCB00", fontWeight:600 }}>{action.actionRequired}</p>
          </div>
          {(action.blocker || action.strategicImpact) && (
            <button onClick={() => setExpanded(!expanded)}
              style={{ display:"flex", alignItems:"center", gap:4, fontSize:8, color:"#475569",
                background:"none", border:"none", cursor:"pointer", flexShrink:0 }}
              data-testid={`${testId}-expand`}>
              {expanded ? <ChevronUp size={10}/> : <ChevronDown size={10}/>}
              Details
            </button>
          )}
        </div>
      </div>

      {expanded && (action.blocker || action.strategicImpact || action.ifNothing) && (
        <div style={{ background:"#060c1a", borderTop:`1px solid ${sev.border}`, padding:"10px 14px" }}>
          {action.strategicImpact && (
            <div style={{ marginBottom:6 }}>
              <p style={{ fontSize:8, fontWeight:700, color:"#FFCB00", textTransform:"uppercase", marginBottom:2 }}>Strategic Impact</p>
              <p style={{ fontSize:9, color:"#94a3b8" }}>{action.strategicImpact}</p>
            </div>
          )}
          {action.blocker && (
            <div style={{ marginBottom:6 }}>
              <div style={{ display:"flex", gap:5 }}>
                <AlertTriangle size={9} color="#f59e0b" style={{ flexShrink:0, marginTop:1 }} />
                <div>
                  <p style={{ fontSize:8, fontWeight:700, color:"#f59e0b", textTransform:"uppercase", marginBottom:1 }}>Current Blocker</p>
                  <p style={{ fontSize:9, color:"#94a3b8" }}>{action.blocker}</p>
                </div>
              </div>
            </div>
          )}
          {action.ifNothing && (
            <div style={{ background:"#7f1d1d20", border:"1px solid #ef444425", borderRadius:5, padding:"6px 10px" }}>
              <p style={{ fontSize:8, fontWeight:700, color:"#ef4444", marginBottom:1 }}>If No Action Taken</p>
              <p style={{ fontSize:9, color:"#f87171" }}>{action.ifNothing}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ExecutiveActionPanel({ actions = [], title = "Executive Action Intelligence", compact = false }) {
  if (!actions || actions.length === 0) return null;
  return (
    <div style={{ background:"#0d1423", border:"1px solid #1e293b", borderRadius:8, padding:compact?12:16 }}
      data-testid="executive-action-panel">
      <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:compact?8:12 }}>
        <Zap size={12} color="#FFCB00" />
        <p style={{ fontSize:9, fontWeight:700, color:"#94a3b8", textTransform:"uppercase", letterSpacing:".07em" }}>{title}</p>
        <div style={{ display:"flex", gap:4, marginLeft:"auto" }}>
          {["Critical","High","Medium"].map(sev => {
            const count = actions.filter(a => a.severity === sev).length;
            return count > 0 ? (
              <span key={sev} style={{ fontSize:7, fontWeight:700, padding:"1px 5px", borderRadius:3,
                background:SEVERITY_STYLE[sev].label+"15", color:SEVERITY_STYLE[sev].label }}>
                {count} {sev}
              </span>
            ) : null;
          })}
        </div>
      </div>
      <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
        {actions.map((a, i) => <ExecutiveAction key={i} action={a} />)}
      </div>
    </div>
  );
}
